import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import type { ApiResponse, PaginatedResponse, Pagination } from "@/app/types";
import api from "@/app/services/apiNir.ts";
import type { AxiosResponse } from "axios";
import {ComplianceStatus, Entity, EntityFull, EntityStatusLog} from "@/domains/home/services/registry/types";

export const useRegistryStore = defineStore("non-individuals", () => {
  const okResponse:Ref<object|undefined> = ref()
  const pagination:Ref<Pagination|undefined> = ref()
  const entities:Ref<Entity[]|undefined> = ref()
  const complianceStatuses:Ref<ComplianceStatus[]|undefined> = ref()
  const entityStatuses: Ref<EntityStatusLog[] | undefined> = ref()
  const entity:Ref<EntityFull|undefined> = ref()

  const fetchEntities = async (ern:string, name:string, typeId:string, subTypeId:string, institutionId:string, status:string, page:number, limit:number) => {
    let path = `/v1/registry?page=${page}&limit=${limit}`
    if (ern){
      path += `&ern=${ern}`
    }
    if(name){
      path += `&name=${name}`
    }
    if (typeId) {
      path += `&typeId=${typeId}`
    }
    if (subTypeId) {
      path += `&subTypeId=${subTypeId}`
    }
    if (status) {
      path += `&status=${status}`
    }
    if (institutionId) {
      path += `&institutionId=${institutionId}`
    }
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<PaginatedResponse<Entity>>>) => {
        entities.value = response.data.data.content
        pagination.value = response.data.data.pagination
      })
  }

  const fetchEntityStatuses = async (ern: string, page: number, limit: number) => {
    let path = `/v1/registry/entity/statuses?page=${page}&limit=${limit}`
    if (ern) path += `&ern=${ern}`
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<PaginatedResponse<EntityStatusLog>>>) => {
        entityStatuses.value = response.data.data.content
      })
  }

  const fetchEntity = async (ern:string) => {
    let path = `/v1/registry/file?ern=${ern}`
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<EntityFull>>) => {
        entity.value = response.data.data
      })
  }

  const deactivateEntity = async (payload: object) => {
    return api.put('/v1/registry/deactivate', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => { okResponse.value = response.data.data })
  }

  const reactivateEntity = async (payload: object) => {
    return api.put('/v1/registry/reactivate', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => { okResponse.value = response.data.data })
  }

  const deregisterEntity = async (payload: object) => {
    return api.put('/v1/registry/deregister', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => { okResponse.value = response.data.data })
  }

  const setCompliance = async (payload: object) => {
    return api.put('/v1/registry/set-compliance', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => { okResponse.value = response.data.data })
  }

  const setRegistrationStatus = async (payload: { ern: string; status: string }) => {
    return api.put('/v1/registry/set-registration-status', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => { okResponse.value = response.data.data })
  }

  const updateAccount = async (payload: object) => {
    return api.put('/v1/registry/entity/account', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => { okResponse.value = response.data.data })
  }

  const createComplianceStatus = async (payload:object) => {
    return api.post(`/v1/registry/compliance/statuses`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const updateComplianceStatus = async (payload:object) => {
    return api.put(`/v1/registry/compliance/statuses`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const fetchComplianceStatuses = async (institutionId:string, page:number, limit:number) => {
    let path = `/v1/registry/compliance/statuses?page=${page}&limit=${limit}`
    if (institutionId) {
      path += `&id=${institutionId}`
    }
    return api.get(path)
        .then((response: AxiosResponse<ApiResponse<PaginatedResponse<ComplianceStatus>>>) => {
          complianceStatuses.value = response.data.data.content
          pagination.value = response.data.data.pagination
        })
  }

  return{
    okResponse,
    pagination,
    entity,
    entities,
    complianceStatuses,
    entityStatuses,
    fetchEntities,
    fetchEntity,
    fetchEntityStatuses,
    fetchComplianceStatuses,
    createComplianceStatus,
    updateComplianceStatus,
    deactivateEntity,
    reactivateEntity,
    deregisterEntity,
    setCompliance,
    setRegistrationStatus,
    updateAccount,
  }
})